import { ManagerUpdatesPageListInvestmentsForManagerUpdateQuery, Security_Status } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { Investment } from '@/components/dealCatalog-page/types';
import { Loader } from '@/components/loaders/loader';
import { allStrategiesTableColumns } from '@/components/managerUpdates-page/constants';
import { isMoreThan90DaysInPast } from '@/components/managerUpdates-page/utils';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import { TableBodyWrapper, TableRowCell, TableRowWrapper, TableWrapper, TextCell } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort } from '@/components/table/types';
import { dateFormatter } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { format } from 'date-fns';
import styled from 'styled-components';

type TableProp = {
  sort: ISort;
  tableData: ManagerUpdatesPageListInvestmentsForManagerUpdateQuery['listInvestmentsForManagerUpdate']['data'];
  loading: boolean;
  forceRecalculateTableFiltersHeight: any;
  setSort: (value: ISort) => void;
  openManagerUpdateDetailsPage: ({ strategyId, documentRecordId }: { strategyId: string; documentRecordId?: string }) => void;
};

const tooltipContent = (row: ManagerUpdatesPageListInvestmentsForManagerUpdateQuery['listInvestmentsForManagerUpdate']['data'][number]) => {
  return (
    <TableBodyWrapper>
      <TableRowCell width={100}>
        <TextCell>Draft: {row.documentRecordMetadata?.num_of_mu_draft}</TextCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TextCell>Published: {row.documentRecordMetadata?.num_of_mu_published}</TextCell>
      </TableRowCell>
    </TableBodyWrapper>
  );
};

export const Table = ({ tableData, sort, loading, forceRecalculateTableFiltersHeight, setSort, openManagerUpdateDetailsPage }: TableProp) => {
  return (
    <TableWrapper padding="0">
      <TableHeader
        refetch={() => {}}
        columns={allStrategiesTableColumns}
        savedSort={sort}
        savedSetSort={setSort}
        forceRecalculateTableFiltersHeight={forceRecalculateTableFiltersHeight}
      />
      <TableBodyWrapper>
        {loading ? (
          <Loader />
        ) : (
          <>
            {!tableData.length ? (
              <NoResults />
            ) : (
              <>
                {tableData.map((row) => (
                  <TableRowWrapper key={row.id}>
                    <TableRowCell width={allStrategiesTableColumns[0].width}>
                      <RedirectTitleCell onClick={() => openManagerUpdateDetailsPage({ strategyId: row.id })}>{row.name}</RedirectTitleCell>
                    </TableRowCell>
                    <TableRowCell width={allStrategiesTableColumns[1].width}>
                      <TextCell>{row.managerId}</TextCell>
                    </TableRowCell>
                    <TableRowCell width={allStrategiesTableColumns[2].width}>
                      <TextCell>{row?.workflowInstance?.currentState?.label}</TextCell>
                    </TableRowCell>
                    <TableRowCell width={allStrategiesTableColumns[3].width}>
                      <TextCell>
                        <CellWithTooltip>
                          {row?.documentRecordMetadata?.num_of_mu}
                          <TooltipWrapper id="infoWrap">
                            <Tooltip title={tooltipContent(row)}>
                              <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                            </Tooltip>
                          </TooltipWrapper>
                        </CellWithTooltip>
                      </TextCell>
                    </TableRowCell>
                    <TableRowCell width={allStrategiesTableColumns[4].width}>
                      {row?.documentRecordMetadata?.new_ai_suggestions_date ? (
                        <RedirectTitleCell
                          onClick={() =>
                            openManagerUpdateDetailsPage({
                              strategyId: row.id,
                              documentRecordId: row?.documentRecordMetadata?.new_ai_suggestions_document_id
                            })
                          }
                        >
                          {dateFormatter(format(new Date(row.documentRecordMetadata.new_ai_suggestions_date), 'yyyy-MM-dd'), 'yyyy-MM-dd')}
                        </RedirectTitleCell>
                      ) : (
                        <TextCell>--</TextCell>
                      )}
                    </TableRowCell>
                    <TableRowCell width={allStrategiesTableColumns[5].width}>
                      <LastPublishDateCell isMoreThan90DaysInPast={isMoreThan90DaysInPast(row?.documentRecordMetadata?.last_mu_publish_date)}>
                        {row?.documentRecordMetadata?.last_mu_publish_date &&
                          dateFormatter(format(new Date(row.documentRecordMetadata.last_mu_publish_date), 'yyyy-MM-dd'), 'yyyy-MM-dd')}
                      </LastPublishDateCell>
                    </TableRowCell>
                  </TableRowWrapper>
                ))}
              </>
            )}
          </>
        )}
      </TableBodyWrapper>
    </TableWrapper>
  );
};

const LastPublishDateCell = styled(TextCell)<{ isMoreThan90DaysInPast: boolean }>`
  font-weight: 600;
  color: ${({ isMoreThan90DaysInPast }) => (isMoreThan90DaysInPast ? themeVar('context.error') : themeVar('font.base'))};
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipWrapper = styled.div`
  opacity: 0;
`;
